import { useContext, useEffect, useRef, useState } from "react"
import { priorityColors } from "../../../entries"
import { RxPlusCircled } from "react-icons/rx"
import { pageLoadingActive } from "../../../components/Background"
import { CiEdit } from "react-icons/ci"
import { SetupElementEdit } from "../../../utils/setupUtils"
import { requestDeletePriorityLevel, requestEditPriorityLevel, requestPriorityLevels, requestSubmitNewPriorityLevel } from "../../../requests/setup_requests"
import { ContentContext } from "../../../contexts/ContentContext"
import { PageHeader } from "../../../components/HeaderComponents"
import { useTranslation } from "react-i18next"
import { PermissionContext } from "../../../contexts/PermissionContexts"
import { useNavigate } from "react-router-dom"
import { MdDone } from "react-icons/md"


export const PrioritySetup = () => {
    const { t } = useTranslation('settings')
    const { openErrorModal, priorities, setPriorityLevels } = useContext(ContentContext)
    const { hasPermission } = useContext(PermissionContext)

    const [openNew, setOpenNew] = useState(false)
    const [editInProgress, setEditInProgress] = useState(false);

    const navigator = useNavigate()


    const handleOpenNew = () => {
        setOpenNew(true)
    }

    useEffect(() => {

        if (!hasPermission("setup:view")) {
            navigator("/admin/dashboard")
            return
        }

        pageLoadingActive(true)
        requestPriorityLevels({})
            .then((data) => {
                setPriorityLevels(data.priority_levels)
                pageLoadingActive(false)
            })
            .catch((error) => {
                console.error(error.status, error.message)
                openErrorModal(error)
                pageLoadingActive(false)
            })
    }, [])


    useEffect(() => {
        if (openNew) {
            setEditInProgress(true)
        }
        else {
            setEditInProgress(false)
        }
    }, [openNew])

    if (!hasPermission("setup:view")) {
        return <></>
    }

    return (
        <div className="flex flex-col w-full bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-300 border-gray-300 dark:border-gray-600 rounded-md px-4 py-3 gap-3">
            <PageHeader pageTitle={t("Priority Setup")} />
            <span>{t('Set up the priority parameters.')}</span>
            <div className="grid grid-cols-4 w-full gap-3 items-center">
                <span className="label-text text-base text-gray-800 dark:text-gray-300 font-semibold">
                    {t('Title')}
                </span>
                <span className="label-text text-base text-gray-800 dark:text-gray-300 font-semibold">
                    {t('Value')}
                </span>
                <span className="label-text text-base text-gray-800 dark:text-gray-300 font-semibold">
                    {t('Default')}
                </span>
            </div>

            {
                Object.values(priorities).map((priority, index) =>
                    <PriorityElement key={priority.id} props={priority} setPriorityLevels={setPriorityLevels} editInProgress={editInProgress} setEditInProgress={setEditInProgress} />
                )
            }

            {
                !openNew ? 
                (hasPermission("setup:create") &&
                    <div className="flex w-full justify-center mt-4">
                        <button className="btn w-full btn-md h-9 p-0 text-primary dark:text-blue-600 bg-white dark:bg-gray-700 hover:text-white hover:bg-primary  border-gray-500 dark:border-gray-500 hover:border-gray-300 dark:hover:border-gray-500"
                            disabled={editInProgress}
                            onClick={handleOpenNew}
                        >
                            <RxPlusCircled size={30} />
                        </button>
                    </div>)
                    :
                    <SetupElementEdit
                        key={"$$$$$$$$$"}
                        props
                        setOpen={setOpenNew}
                        setElement={setPriorityLevels}
                        edit={false}
                        styles={priorityColors}
                        requestNewElement={requestSubmitNewPriorityLevel}
                        requestEditElement={requestEditPriorityLevel}
                        requestDeleteElement={requestDeletePriorityLevel}
                    />
            }
        </div>
    )
}


const PriorityElement = ({ props, setPriorityLevels, editInProgress, setEditInProgress }) => {
    const { hasPermission } = useContext(PermissionContext)

    const [edit, setEdit] = useState(false)
    const [newElements, setNewElements] = useState(null)
    const handleOpenEdit = () => {
        setEdit(true)
    }

    useEffect(() => {
        if (edit) {
            setEditInProgress(true)
        }
        else {
            setEditInProgress(false)

        }
    }, [edit])


    useEffect(() => {
        if (newElements !== null) {
            setPriorityLevels(newElements)
            setNewElements(null)
        }
    }, [newElements])


    return (
        !edit ?
            <div className="grid grid-cols-4 w-full gap-3 items-center group hover:bg-gray-100 dark:hover:bg-gray-600 rounded-sm py-1">
                <div className={`${props.style} rounded-full select-none px-[6px] min-w-16 p-1 max-w-max text-center truncate text-nowrap`}>
                    <span>{props.title}</span>
                </div>
                <span>{props.value}</span>
                <div>{props.default ? <MdDone /> : <></>}</div>
                {
                    hasPermission("setup:edit") &&
                    <button className="btn btn-sm w-8 opacity-0 group-hover:opacity-100 transition-all duration-300 disabled:invisible ease-in-out border-none bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-300 hover:bg-primary dark:hover:bg-blue-600 hover:text-white p-0"
                        disabled={editInProgress}
                        onClick={handleOpenEdit}
                    >
                        <CiEdit size={24} />
                    </button>
                }
            </div >
            :
            <SetupElementEdit
                key={props.id}
                props={props}
                setOpen={setEdit}
                setElement={setNewElements}
                edit={true}
                hasDefault={true}
                styles={priorityColors}
                requestNewElement={requestSubmitNewPriorityLevel}
                requestEditElement={requestEditPriorityLevel}
                requestDeleteElement={requestDeletePriorityLevel}
            />

    )
}


